import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EMPTY,
  filter,
  finalize,
  of,
  repeat,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { ActionQueueService } from './action-queue.service';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ActionItemDrawerService } from '@main-client/src/app/members/automated-user-management/action-item-drawer/action-item-drawer.service';

import {
  ACTION_ITEM_STATUS_COMPLETED,
  ActionItem,
} from '@libs/src/models/action-item.model';
import {
  ACTION_QUEUE_DEACTIVATE,
  ACTION_QUEUE_DELETE,
  ACTION_QUEUE_INVITE,
  ACTION_QUEUE_STATUS_CANCELLED,
  ACTION_QUEUE_STATUS_COMPLETED,
  ACTION_QUEUE_STATUS_ERROR,
  ACTION_QUEUE_STATUS_IN_PROGRESS,
  ACTION_QUEUE_STATUS_INITIALIZING,
  ACTION_QUEUE_STATUS_PENDING,
  ACTION_QUEUE_STATUS_SEMI_COMPLETED,
  ACTION_QUEUE_STATUSES,
  ACTION_QUEUE_TYPES,
  ACTION_QUEUE_UPDATE,
  ActionQueue,
} from '@libs/src/models/action-queue.model';
import { Group } from '@libs/src/models/group.model';

import merge from 'lodash-es/merge';

interface State {
  [key: string]: {
    actionItems: ActionItem[];
    actionQueue: ActionQueue;
    activeActionItems: number;
    completedActionItemsCount: number;
  };
}

const titleOptions = {
  deactivate: $localize`:@@actionItemDrawer.title.deactivate:Deactivate members`,
  delete: $localize`:@@actionItemDrawer.title.delete:Delete members`,
  invite: $localize`:@@actionItemDrawer.title.invite:Invite members`,
  update: $localize`:@@actionItemDrawer.title.update:Update members`,
};

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-action-queue',
  templateUrl: './action-queue.component.html',
})
export class ActionQueueComponent implements OnInit {
  private readonly actionQueuePolling$ = new Subject<void>();
  private readonly actionItemsSubscriptions: Map<string, Subscription> =
    new Map();
  actionTypes = [
    ACTION_QUEUE_INVITE,
    ACTION_QUEUE_UPDATE,
    ACTION_QUEUE_DEACTIVATE,
    ACTION_QUEUE_DELETE,
  ];
  actionQueueDescriptions: { [key: string]: string };
  actionQueueTitles: { [key: string]: string };
  state: State = {
    [ACTION_QUEUE_DEACTIVATE]: {
      actionItems: [],
      actionQueue: null,
      activeActionItems: 0,
      completedActionItemsCount: 0,
    },
    [ACTION_QUEUE_DELETE]: {
      actionItems: [],
      actionQueue: null,
      activeActionItems: 0,
      completedActionItemsCount: 0,
    },
    [ACTION_QUEUE_INVITE]: {
      actionItems: [],
      actionQueue: null,
      activeActionItems: 0,
      completedActionItemsCount: 0,
    },
    [ACTION_QUEUE_UPDATE]: {
      actionItems: [],
      actionQueue: null,
      activeActionItems: 0,
      completedActionItemsCount: 0,
    },
  };
  banner: {
    buttonText?: string;
    description: string;
    imageColor?: string;
    imageName: string;
    title: string;
    type: string;
  };
  fileName: string;
  flatFileGeneration: number;
  group: Group;
  groupName: Group['name'];
  groupId: Group['_id'];
  isLoading = true;
  showErrorBanner = false;
  titleOptions: { [key: string]: string } = titleOptions;

  constructor(
    private readonly actionItemDrawerService: ActionItemDrawerService,
    private readonly actionQueueService: ActionQueueService,
    private readonly datePipe: DatePipe,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly groupsBackend: GroupsBackend,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.groupId = this.route.snapshot.queryParamMap.get('groupId');
    this.groupName = this.route.snapshot.queryParamMap.get('groupName');
    this.fileName = this.route.snapshot.queryParamMap.get('fileName');
    this.flatFileGeneration = Number(
      this.route.snapshot.queryParamMap.get('flatFileGeneration'),
    );
    this.groupsBackend
      .getGroup({ _id: this.groupId })
      .pipe(
        tap((group) => {
          this.group = group;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.actionQueueTitles = {
      deactivate: $localize`:@@csvActionItemQueues.pendingDeactivate.title:Deactivate member profiles`,
      delete: $localize`:@@csvActionItemQueues.pendingDelete.title:Delete members accounts`,
      invite: $localize`:@@csvActionItemQueues.pendingInvite.title:Invite members`,
      update: $localize`:@@csvActionItemQueues.pendingUpdateAndReactivate.title:Update and reactivate member profiles`,
    };
    this.actionQueueDescriptions = {
      deactivate: $localize`:@@csvActionItemQueues.pendingDeactivate.descriptionTitle:Set profiles to inactive using "status" column in uploaded membership file`,
      delete: $localize`:@@csvActionItemQueues.pendingDelete.description:Remove members from Hub, or delete their accounts permanently`,
      invite: $localize`:@@csvActionItemQueues.pendingInvite.description:Invite members in the membership file without active accounts to ${this.groupName}`,
      update: $localize`:@@csvActionItemQueues.pendingUpdateAndReactivate.description:Refresh existing profiles and reactivate previously inactive members using data from uploaded membership file`,
    };
    this.manageInitialProcessing();
  }

  startActionItemsPolling() {
    Object.keys(this.state).forEach((action) => {
      const actionQueue = this.state[action].actionQueue;
      const isPollingNeeded =
        actionQueue?.status === ACTION_QUEUE_STATUS_IN_PROGRESS;
      if (!isPollingNeeded) {
        this.isLoading = false;
        return;
      }
      if (
        isPollingNeeded &&
        !this.actionItemsSubscriptions.has(actionQueue._id)
      ) {
        const subscription = this.actionQueueService
          .pollActionItemsForActionQueue(this.state[action].actionQueue._id)
          .pipe(
            finalize(() =>
              this.actionQueueService
                .getActionItemsForActionQueue(actionQueue._id)
                .pipe(
                  tap(({ actionItems }) => {
                    this.state[action].actionItems = actionItems;
                    this.state[action].completedActionItemsCount =
                      actionItems.filter(
                        (actionItem) =>
                          actionItem.status === ACTION_ITEM_STATUS_COMPLETED,
                      ).length;
                    this.setActionQueueTitles(
                      action,
                      this.state[action].actionQueue.updated_at,
                    );
                    this.actionItemsSubscriptions
                      .get(actionQueue._id)
                      ?.unsubscribe();
                    this.actionItemsSubscriptions.delete(actionQueue._id);
                  }),
                ),
            ),
            takeUntil(merge(this.actionQueuePolling$, this.destroy$)),
          )
          .subscribe(({ actionItems }) => {
            if (!this.state[action]) {
              return;
            }
            this.state[action].actionItems = actionItems;
            this.state[action].completedActionItemsCount = actionItems.filter(
              (actionItem) =>
                actionItem.status === ACTION_ITEM_STATUS_COMPLETED,
            ).length;
          });
        this.actionItemsSubscriptions.set(
          this.state[action].actionQueue._id,
          subscription,
        );
        this.isLoading = true;
      }
    });
  }

  startActionQueuesPolling() {
    return this.actionQueueService.pollActionQueues({
      fileName: this.fileName,
      flatFileGeneration: this.flatFileGeneration,
      groupId: this.groupId,
    });
  }

  setActionQueues(actionQueues: ActionQueue[]) {
    actionQueues.forEach((actionQueue) => {
      if (!this.state[actionQueue.action]) {
        return;
      }
      this.state[actionQueue.action].actionQueue = actionQueue;
    });
  }

  setActionItemsForActionQueues(actionItemsForActionQueues: {
    [key: string]: ActionItem[];
  }) {
    Object.keys(actionItemsForActionQueues).forEach((action) => {
      this.state[action].actionItems = actionItemsForActionQueues[action];
      this.state[action].completedActionItemsCount = actionItemsForActionQueues[
        action
      ].filter(
        (actionItem) => actionItem.status === ACTION_ITEM_STATUS_COMPLETED,
      ).length;
      this.state[action].activeActionItems = [
        ACTION_QUEUE_STATUS_INITIALIZING,
        ACTION_QUEUE_STATUS_PENDING,
        ACTION_QUEUE_STATUS_IN_PROGRESS,
        ACTION_QUEUE_STATUS_COMPLETED,
        ACTION_QUEUE_STATUS_SEMI_COMPLETED,
      ].includes(this.state[action].actionQueue.status)
        ? actionItemsForActionQueues[action].length
        : this.state[action].activeActionItems;
    });
  }

  checkAllActionQueuesHaveInitialized() {
    return Object.keys(this.state).every((action) => {
      const { actionQueue } = this.state[action];
      return (
        actionQueue &&
        ![
          ACTION_QUEUE_STATUS_INITIALIZING,
          ACTION_QUEUE_STATUS_CANCELLED,
          ACTION_QUEUE_STATUS_COMPLETED,
          ACTION_QUEUE_STATUS_ERROR,
          ACTION_QUEUE_STATUS_SEMI_COMPLETED,
        ].includes(actionQueue.status)
      );
    });
  }

  checkSomeActionQueuesInProgress() {
    return Object.keys(this.state).some(
      (action) =>
        this.state[action].actionQueue &&
        this.state[action].actionQueue?.status ===
          ACTION_QUEUE_STATUS_IN_PROGRESS,
    );
  }

  checkAllActionQueuesAreProcessed(actionQueues: ActionQueue[]) {
    return actionQueues.every((actionQueue) =>
      [
        ACTION_QUEUE_STATUS_CANCELLED,
        ACTION_QUEUE_STATUS_COMPLETED,
        ACTION_QUEUE_STATUS_ERROR,
        ACTION_QUEUE_STATUS_SEMI_COMPLETED,
      ].includes(actionQueue.status),
    );
  }

  checkNotActiveActionQueues(actionQueues: ActionQueue[]) {
    return actionQueues.every((actionQueue) =>
      [
        ACTION_QUEUE_STATUS_CANCELLED,
        ACTION_QUEUE_STATUS_COMPLETED,
        ACTION_QUEUE_STATUS_PENDING,
        ACTION_QUEUE_STATUS_ERROR,
        ACTION_QUEUE_STATUS_SEMI_COMPLETED,
      ].includes(actionQueue.status),
    );
  }

  getProcessedActionQueues(actionQueues: ActionQueue[]) {
    return actionQueues.filter((actionQueue) =>
      [
        ACTION_QUEUE_STATUS_CANCELLED,
        ACTION_QUEUE_STATUS_COMPLETED,
        ACTION_QUEUE_STATUS_ERROR,
        ACTION_QUEUE_STATUS_SEMI_COMPLETED,
      ].includes(actionQueue.status),
    );
  }

  getInitializedActionQueues() {
    return Object.keys(this.state)
      .map((action) => {
        if (
          ACTION_QUEUE_STATUS_INITIALIZING !==
          this.state[action].actionQueue?.status
        ) {
          return this.state[action].actionQueue;
        }
        return null;
      })
      .filter(Boolean);
  }

  getActionQueuesFromState() {
    return Object.keys(this.state).map(
      (action) => this.state[action].actionQueue,
    );
  }

  manageProcessingPolling() {
    this.startActionQueuesPolling()
      .pipe(
        map(({ actionQueues }) => {
          this.setActionQueues(actionQueues);
          const errorActionQueue = actionQueues.find(
            (queue) => ACTION_QUEUE_STATUS_ERROR === queue.status,
          );
          if (errorActionQueue && 0 < errorActionQueue?.error_causes.length) {
            const errorList = errorActionQueue.error_causes
              .map((errorCause) => errorCause.message)
              .join(', ');
            this.banner = {
              description: $localize`:@@csvActionItemQueues.actionQueueErrors.description:Errors: ${errorList}`,
              imageColor: 'color-orange',
              imageName: 'error',
              title: $localize`:@@csvActionItemQueues.actionQueueErrors.title:Resolve errors before approving members updates`,
              type: 'error',
            };
            this.showErrorBanner = true;
          }
          if (this.checkSomeActionQueuesInProgress()) {
            this.startActionItemsPolling();
          }
          const processedActionQueues =
            this.getProcessedActionQueues(actionQueues);
          if (processedActionQueues.length) {
            this.actionQueueService
              .getActionItemsForActionQueues(processedActionQueues)
              .pipe(
                filter((response) => !!response),
                tap((actionItemsForActionQueues) => {
                  this.setActionItemsForActionQueues(
                    actionItemsForActionQueues,
                  );
                  actionQueues.forEach((queue) => {
                    if (ACTION_QUEUE_STATUS_COMPLETED === queue.status) {
                      this.setActionQueueTitles(queue.action, queue.updated_at);
                    }
                    this.actionItemsSubscriptions.get(queue._id)?.unsubscribe();
                    this.actionItemsSubscriptions.delete(queue._id);
                  });
                }),
              )
              .subscribe();
          }
          if (this.actionTypes.length === processedActionQueues.length) {
            this.actionQueuePolling$.next();
          }
        }),
        catchError(() => EMPTY),
        repeat(),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(merge(this.actionQueuePolling$, this.destroy$)),
      )
      .subscribe();
  }

  manageInitialPolling() {
    return this.startActionQueuesPolling().pipe(
      map(({ actionQueues }) => {
        this.setActionQueues(actionQueues);
        const errorActionQueue = actionQueues.find(
          (queue) => ACTION_QUEUE_STATUS_ERROR === queue.status,
        );
        if (errorActionQueue && 0 < errorActionQueue?.error_causes.length) {
          const errorList = errorActionQueue.error_causes
            .map((errorCause) => errorCause.message)
            .join(', ');
          this.banner = {
            description: $localize`:@@csvActionItemQueues.actionQueueErrors.description:Errors: ${errorList}`,
            imageColor: 'color-orange',
            imageName: 'error',
            title: $localize`:@@csvActionItemQueues.actionQueueErrors.title:Resolve errors before approving members updates`,
            type: 'error',
          };
          this.showErrorBanner = true;
        }
        if (this.checkAllActionQueuesAreProcessed(actionQueues)) {
          return;
        }
        if (
          this.checkAllActionQueuesHaveInitialized() &&
          !this.checkSomeActionQueuesInProgress()
        ) {
          this.actionQueuePolling$.next();
        }
        actionQueues.forEach((queue) => {
          if (
            ACTION_QUEUE_STATUS_COMPLETED === queue.status ||
            ACTION_QUEUE_STATUS_SEMI_COMPLETED === queue.status
          ) {
            this.setActionQueueTitles(queue.action, queue.updated_at);
          }
        });
        const initializedActionQueues = this.getInitializedActionQueues();
        if (initializedActionQueues.length) {
          this.actionQueueService
            .getActionItemsForActionQueues(initializedActionQueues)
            .pipe(
              filter((response) => !!response),
              tap((actionItemsForActionQueues) => {
                this.setActionItemsForActionQueues(actionItemsForActionQueues);
              }),
            )
            .subscribe();
        }
        if (this.checkNotActiveActionQueues(actionQueues)) {
          this.actionQueuePolling$.next();
        }
      }),
      catchError(() => EMPTY),
      repeat(),
      takeUntil(merge(this.actionQueuePolling$, this.destroy$)),
    );
  }

  manageInitialProcessing() {
    this.actionQueueService
      .getActionQueues({
        fileName: this.fileName,
        flatFileGeneration: this.flatFileGeneration,
        groupId: this.groupId,
      })
      .pipe(
        concatMap(({ actionQueues }) => {
          this.setActionQueues(actionQueues);
          const errorActionQueue = actionQueues.find(
            (queue) => ACTION_QUEUE_STATUS_ERROR === queue.status,
          );
          if (errorActionQueue && 0 < errorActionQueue?.error_causes.length) {
            const errorList = errorActionQueue.error_causes
              .map((errorCause) => errorCause.message)
              .join(', ');
            this.banner = {
              description: $localize`:@@csvActionItemQueues.actionQueueErrors.description:Errors: ${errorList}`,
              imageColor: 'color-orange',
              imageName: 'error',
              title: $localize`:@@csvActionItemQueues.actionQueueErrors.title:Resolve errors before approving members updates`,
              type: 'error',
            };
            this.showErrorBanner = true;
            return of();
          }
          const allActionQueuesHaveInitialized =
            this.checkAllActionQueuesHaveInitialized();
          if (allActionQueuesHaveInitialized) {
            const initializedActionQueues = this.getActionQueuesFromState();
            initializedActionQueues.forEach((action) => {
              if (ACTION_QUEUE_STATUS_COMPLETED === action.status) {
                this.setActionQueueTitles(action.action, action.updated_at);
              }
            });
            return this.actionQueueService
              .getActionItemsForActionQueues(initializedActionQueues)
              .pipe(
                concatMap((actionItemsForActionQueues) => {
                  this.setActionItemsForActionQueues(
                    actionItemsForActionQueues,
                  );
                  const areAllEmpty = Object.keys(this.state).every(
                    (action) => 0 === this.state[action].actionItems.length,
                  );
                  if (areAllEmpty) {
                    this.banner = {
                      description: $localize`:@@csvActionItemQueues.emptyFileError.description:The imported file does not contain any member updates. Upload the file again to get member updates.`,
                      imageColor: 'color-orange',
                      imageName: 'error',
                      title: $localize`:@@csvActionItemQueues.processingError.title:Cannot generate any member updates`,
                      type: 'error',
                    };
                    this.showErrorBanner = true;
                    return of();
                  }
                  return this.manageInitialPolling();
                }),
              );
          }
          const initializedActionQueues = this.getInitializedActionQueues();
          if (initializedActionQueues.length) {
            initializedActionQueues.forEach((action) => {
              if (ACTION_QUEUE_STATUS_COMPLETED === action.status) {
                this.setActionQueueTitles(action.action, action.updated_at);
              }
            });
            return this.actionQueueService
              .getActionItemsForActionQueues(initializedActionQueues)
              .pipe(
                concatMap((actionItemsForActionQueues) => {
                  this.setActionItemsForActionQueues(
                    actionItemsForActionQueues,
                  );
                  return this.manageInitialPolling();
                }),
              );
          }
          return this.manageInitialPolling();
        }),
        catchError(() => {
          this.banner = {
            description: $localize`:@@csvActionItemQueues.processingError.description:An error has occurred when processing this file. <a class="color-blue">Contact 10KC</a> to troubleshoot.`,
            imageColor: 'color-orange',
            imageName: 'error',
            title: $localize`:@@csvActionItemQueues.processingError.title:Cannot generate any member updates`,
            type: 'error',
          };
          this.showErrorBanner = true;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  openActionItemDrawer(
    actionQueueType: ActionQueue['action'],
    actionQueueId: ActionQueue['_id'],
    actionQueueStatus: ActionQueue['status'],
  ) {
    const title = this.titleOptions[actionQueueType];
    this.actionItemDrawerService
      .openDrawer(
        {
          actionQueueId,
          actionQueueStatus,
          actionQueueType,
          actionItems: this.state[actionQueueType].actionItems,
          group: this.group,
        },
        title,
      )
      .afterClose.pipe(
        filter((response: { status: ActionQueue['status'] }) => !!response),
        tap(
          (response: {
            activeItemCount: number;
            date: Date;
            status: ActionQueue['status'];
          }) => {
            this.state[actionQueueType].activeActionItems =
              response.activeItemCount;
            this.manageProcessingPolling();
            if (
              ACTION_QUEUE_STATUSES.ACTION_QUEUE_STATUS_COMPLETED ===
              response.status
            ) {
              this.setActionQueueTitles(actionQueueType, response.date);
            }
          },
        ),
      )
      .subscribe();
  }

  setActionQueueTitles(actionType: ActionQueue['action'], date: Date) {
    const readableDate = this.datePipe.transform(date, 'longDate');
    if (ACTION_QUEUE_TYPES.ACTION_QUEUE_DELETE === actionType) {
      this.actionQueueTitles[actionType] =
        $localize`:@@csvActionItemQueues.completedDelete.title:Deleted member accounts on ${readableDate}`;
      return;
    }
    if (ACTION_QUEUE_TYPES.ACTION_QUEUE_DEACTIVATE === actionType) {
      this.actionQueueTitles[actionType] =
        $localize`:@@csvActionItemQueues.completedDeactivate.title:Deactivated member accounts on ${readableDate}`;
      return;
    }
    this.actionQueueTitles[actionType] =
      ACTION_QUEUE_TYPES.ACTION_QUEUE_INVITE === actionType
        ? $localize`:@@csvActionItemQueues.completedInvite.title:Invited members on ${readableDate}`
        : $localize`:@@csvActionItemQueues.completedUpdate.title:Updated member profiles on ${readableDate}`;
  }

  bannerAction(type: string) {
    if ('error' === type) {
      this.router.navigate(['/admin', 'members', 'import-membership-file']);
    }
    this.router.navigate(['/admin', 'members']);
  }
}
